import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useToast } from '../contexts/ToastContext';
import { LIKE_TOAST_EVENT, likeTrack, setTrackNotify } from '../redux/thunk/likesThunk';
import { FOLLOW_TOAST_EVENT, followArtist, setArtistNotify } from '../redux/thunk/followedArtistsThunk';
import { followLabel, setLabelNotify } from '../redux/thunk/followedLabelsThunk';
import { NOTIFY_TOAST_EVENT } from '../utils/notifyToast';
import { QUEUE_TOAST_EVENT, requestOpenQueue } from '../utils/queueAdd';
import { openLikes, openFollowedArtists, isMobileLayout } from '../utils/openInLibrary';
import { track } from '../utils/analytics';

// "<Title> was saved to / removed from your Likes." (removal carries Undo),
// "<Artist> was added to / removed from your Artists." (same),
// bell toggles ("You'll hear about new mixes" / "Notifications turned off" +
// Undo) and "<Title> was added to Next Up." cards.
// The like/follow/notify thunks and the queue helper emit window events (they
// run outside React); this bridge — mounted once inside ToastProvider — turns
// them into card toasts. (Add-to-playlist raises its card directly in
// AddToPlaylistModal, which already lives in React.)
//   • "your Likes"   → Likes sidebar on desktop, Library → Likes page on mobile
//   • "your Artists" → Favourite Artists sidebar / mobile page
//   • "Next Up"      → opens the queue panel
export default function ConfirmationToastBridge() {
    const { card, error } = useToast();
    const dispatch = useDispatch();

    useEffect(() => {
        const duration = () => (isMobileLayout() ? 2800 : 3500);
        // Removal cards carry Undo and dwell longer: the user may need a beat
        // to notice it was a mis-click.
        const undoDuration = () => (isMobileLayout() ? 4000 : 5000);

        const onLike = (e) => {
            const d = e.detail || {};
            const title = d.title || 'Track';
            if (d.status === 'failed') {
                error(`Couldn't save "${title}" to your Likes. Please try again.`);
                return;
            }
            // Liked from the lock screen / native now-playing controls: the
            // app isn't on screen, so a toast would only pile up for later.
            if (d.likeSource === 'lock_screen') return;
            if (d.status === 'unliked') {
                // Unlike is the one removal that's easy to do by accident
                // (tiny toggle next to play, thumb covers it on mobile) and
                // cheap to reverse — so this card carries the fix.
                card({
                    artwork: d.artwork || null,
                    title,
                    message: 'was removed from',
                    duration: undoDuration(),
                    link: {
                        label: 'your Likes',
                        onClick: () => {
                            const layout = openLikes(dispatch);
                            track('likes_sidebar_opened', { from: 'unlike_toast', layout });
                        },
                    },
                    action: {
                        label: 'Undo',
                        onClick: () => {
                            dispatch(likeTrack({ ...(d.undoTrack || { artist: d.artist, title }), likeSource: 'undo' }));
                        },
                    },
                });
                return;
            }
            card({
                artwork: d.artwork || null,
                title,
                message: 'was saved to',
                duration: duration(),
                link: {
                    label: 'your Likes',
                    onClick: () => {
                        const layout = openLikes(dispatch);
                        track('likes_sidebar_opened', { from: 'like_toast', layout });
                    },
                },
            });
        };

        // "your Artists", not the sidebar's full "Favourite Artists" title:
        // next to the Undo pill the card's message line has ~213px, and
        // "was removed from your Favourite Artists." (270px) truncated the
        // link away entirely. Same shorthand as "your Likes" for Liked Tracks.
        const artistsLink = (from) => ({
            label: 'your Artists',
            onClick: () => {
                const layout = openFollowedArtists(dispatch);
                track('artists_sidebar_opened', { from, layout });
            },
        });

        const onFollow = (e) => {
            const d = e.detail || {};
            const name = d.artistName || 'Artist';
            if (d.status === 'failed') {
                error(`Couldn't follow ${name}. Please try again.`);
                return;
            }
            // Bulk flows (onboarding picks, follow-all) would stack a card per artist.
            if (d.quiet || d.from === 'onboarding') return;
            if (d.status === 'unfollowed') {
                // Same reasoning as unlike: one click on Following drops the
                // artist AND their bell, so this card carries the fix.
                card({
                    artwork: d.artwork || null,
                    title: name,
                    message: 'was removed from',
                    duration: undoDuration(),
                    link: artistsLink('unfollow_toast'),
                    action: {
                        label: 'Undo',
                        onClick: () => {
                            if (d.entityType === 'label') {
                                // labels sit on the same Favourite Artists page; the bell rides the follow row too
                                dispatch(followLabel({ ...(d.undoLabel || { name }), from: 'undo' }))
                                    .then((res) => {
                                        if (res?.payload && d.wasNotify && d.undoLabel?.publicId) {
                                            dispatch(setLabelNotify({ labelPublicId: d.undoLabel.publicId, name, notify: true, from: 'undo', quiet: true }));
                                        }
                                    });
                                return;
                            }
                            dispatch(followArtist({ ...(d.undoArtist || { name }), from: 'undo' }))
                                .then((res) => {
                                    // The bell lived on the deleted follow row — restore
                                    // it quietly (its own card would replace this one's
                                    // "added to your Artists" follow-up).
                                    if (res?.payload && d.wasNotify) {
                                        dispatch(setArtistNotify({ artistName: name, notify: true, from: 'undo', quiet: true }));
                                    }
                                });
                        },
                    },
                });
                return;
            }
            card({
                artwork: d.artwork || null,
                title: name,
                message: 'was added to',
                duration: duration(),
                link: artistsLink('follow_toast'),
            });
        };

        // Bell toggles. The bell is a small glyph whose on/off difference is
        // subtle by nature, so every toggle confirms in words; turning it OFF
        // is the quiet, easy-to-miss direction, so that card carries Undo.
        const onNotify = (e) => {
            const d = e.detail || {};
            const isArtist = d.entityType === 'artist';
            const isLabel = d.entityType === 'label';
            const name = d.name || (isArtist ? 'Artist' : isLabel ? 'Label' : 'Track');
            if (d.status === 'failed') {
                error(`Couldn't update notifications for ${name}. Please try again.`);
                return;
            }
            if (d.quiet) return;
            if (d.notify) {
                // The artist bell covers BOTH its notification types — their
                // new mixes and DJs playing their tracks. "Support" is the
                // scene word for the latter (and the app's own: "Supported by
                // N DJs"); the bell tooltip spells it out. Kept short on
                // purpose — "New mixes and when DJs play their tracks"
                // (275px) read as too long.
                card({
                    artwork: d.artwork || null,
                    title: name,
                    message: isArtist ? 'You’ll hear about mixes and support' : isLabel ? 'You’ll hear when big DJs play it' : 'You’ll hear when a DJ plays it',
                    duration: duration(),
                });
                return;
            }
            card({
                artwork: d.artwork || null,
                title: name,
                message: 'Notifications turned off',
                duration: undoDuration(),
                action: {
                    label: 'Undo',
                    onClick: () => {
                        if (isArtist) {
                            dispatch(setArtistNotify({ artistName: d.name, notify: true, from: 'undo', artwork: d.artwork }));
                        } else if (isLabel) {
                            dispatch(setLabelNotify({ labelPublicId: d.labelPublicId, name: d.name, notify: true, from: 'undo', artwork: d.artwork }));
                        } else {
                            dispatch(setTrackNotify({ artist: d.artist, title: d.title, notify: true, from: 'undo', artwork: d.artwork }));
                        }
                    },
                },
            });
        };

        const onQueue = (e) => {
            const d = e.detail || {};
            card({
                artwork: d.artwork || null,
                title: d.title || 'Track',
                message: 'was added to',
                duration: duration(),
                link: {
                    label: 'Next Up',
                    onClick: () => {
                        requestOpenQueue();
                        track('queue_panel_opened', { from: 'queue_toast' });
                    },
                },
            });
        };

        window.addEventListener(LIKE_TOAST_EVENT, onLike);
        window.addEventListener(FOLLOW_TOAST_EVENT, onFollow);
        window.addEventListener(NOTIFY_TOAST_EVENT, onNotify);
        window.addEventListener(QUEUE_TOAST_EVENT, onQueue);
        return () => {
            window.removeEventListener(LIKE_TOAST_EVENT, onLike);
            window.removeEventListener(FOLLOW_TOAST_EVENT, onFollow);
            window.removeEventListener(NOTIFY_TOAST_EVENT, onNotify);
            window.removeEventListener(QUEUE_TOAST_EVENT, onQueue);
        };
    }, [card, error, dispatch]);

    return null;
}
